import { AlertOctagonIcon, CheckCircle2Icon, XCircleIcon } from "lucide-react";

import { trpc } from "@blis/trpc/react";

import { PermissionGate } from "@/components/PermissionGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { ListItemLeft, ListItemRight } from "@/components/ui/list-item";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import type { THold } from "@/lib/types";
import { format } from "@/lib/utils";
import { useAccession } from "../AccessionProvider";
import { HoldDetailDialog } from "../dialogs/HoldDetailDialog";
import { InstrumentResultList } from "../InstrumentResults";
import { ActivityLog } from "./ActivityLog";
import { AccessionComments } from "./Comments";
import { AccessionUploads } from "./Uploads";

export function BottomTabs() {
  const { accession, counts } = useAccession();
  const holds = trpc.accession.holds.useQuery(accession.id);

  return (
    <Tabs defaultSelectedKey="comments" variant="underline">
      <TabsList className="w-full justify-start">
        <PermissionGate requiredPermission="read:accession_comments">
          <TabWithCount id="comments" count={counts.comments}>
            Comments
          </TabWithCount>
        </PermissionGate>
        <PermissionGate requiredPermission="read:accession_notes">
          <TabWithCount id="notes" count={counts.notes}>
            Notes
          </TabWithCount>
        </PermissionGate>
        <PermissionGate requiredPermission="read:holds">
          <TabWithCount id="holds" count={counts.holds}>
            Holds
          </TabWithCount>
        </PermissionGate>
        <PermissionGate requiredPermission="read:instrument_results">
          <TabWithCount id="instrumentResults" count={counts.instrumentResults}>
            Instrument
          </TabWithCount>
        </PermissionGate>
        <PermissionGate requiredPermission="read:accession_uploads">
          <TabWithCount id="uploads" count={counts.uploads}>
            Uploads
          </TabWithCount>
        </PermissionGate>
        <PermissionGate requiredPermission="read:audit_logs">
          <Tab id="activity">Activity</Tab>
        </PermissionGate>
      </TabsList>
      <PermissionGate requiredPermission="read:accession_comments">
        <TabPanel id="comments">
          <AccessionComments accession={accession} isInternal={false} />
        </TabPanel>
      </PermissionGate>
      <PermissionGate requiredPermission="read:accession_notes">
        <TabPanel id="notes">
          <AccessionComments accession={accession} isInternal />
        </TabPanel>
      </PermissionGate>
      <PermissionGate requiredPermission="read:audit_logs">
        <TabPanel id="activity">
          <ActivityLog />
        </TabPanel>
      </PermissionGate>
      <PermissionGate requiredPermission="read:holds">
        <TabPanel id="holds" className="min-h-[300px]">
          <HoldList holds={holds.data || []} isLoading={holds.isPending} />
        </TabPanel>
      </PermissionGate>
      <PermissionGate requiredPermission="read:accession_uploads">
        <TabPanel id="uploads" className="min-h-[300px]">
          <AccessionUploads accession={accession} />
        </TabPanel>
      </PermissionGate>
      <PermissionGate requiredPermission="read:instrument_results">
        <TabPanel id="instrumentResults" className="min-h-[300px]">
          <InstrumentResults />
        </TabPanel>
      </PermissionGate>
    </Tabs>
  );
}

function TabWithCount({
  id,
  count,
  children,
}: {
  id: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Tab id={id}>
      {count > 0 ? (
        <div className="flex items-center gap-1.5">
          {children} <Badge size="sm">{count}</Badge>
        </div>
      ) : (
        children
      )}
    </Tab>
  );
}

function HoldListItem({ hold }: { hold: THold }) {
  const Icon = hold.resolved
    ? CheckCircle2Icon
    : hold.canceled
      ? XCircleIcon
      : AlertOctagonIcon;

  return (
    <>
      <ListItemLeft>
        <Icon
          className={cn(
            "shrink-0 size-4",
            hold.canceled
              ? "text-muted-foreground"
              : hold.resolved
                ? "text-green-600"
                : "text-red-600",
          )}
        />
        <Text size="1" truncate>
          {hold.description}
        </Text>
      </ListItemLeft>
      <ListItemRight>
        <Text size="xs" mono muted className="shrink-0">
          {format(hold.createdAt)}
        </Text>
      </ListItemRight>
    </>
  );
}

function HoldList({
  holds,
  isLoading,
}: {
  holds: THold[];
  isLoading: boolean;
}) {
  return (
    <NavList
      items={holds}
      isLoading={isLoading}
      className="border-b"
      emptyState={
        <EmptyState>
          <EmptyStateIcon>
            <AlertOctagonIcon />
          </EmptyStateIcon>
          <EmptyStateHeading>No holds</EmptyStateHeading>
          <EmptyStateDescription>
            No holds have been placed on this accession.
          </EmptyStateDescription>
        </EmptyState>
      }
    >
      {(hold) => (
        <NavListItem asChild key={hold.id}>
          <HoldDetailDialog hold={hold}>
            <Button
              variant="ghost"
              className="flex items-center w-full h-10 hover:bg-muted/50"
            >
              <HoldListItem hold={hold} />
            </Button>
          </HoldDetailDialog>
        </NavListItem>
      )}
    </NavList>
  );
}

function InstrumentResults() {
  const { accession } = useAccession();
  const instrumentResultSets = trpc.instrumentResults.list.useQuery({
    accessionId: accession.id,
  });

  const resultSets = instrumentResultSets.data || [];

  return <InstrumentResultList resultSets={resultSets} />;
}
